import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import it from './locales/it';
import de from './locales/de';
import en from './locales/en';
import es from './locales/es';

const LOCALES = { it, de, en, es };
const STORAGE_KEY = 'eva_ui_language';
const FALLBACK_LANG = 'en';
const SUPPORTED = ['it', 'de', 'en', 'es'];

function detectBrowserLang() {
  try {
    const nav = navigator.language?.slice(0, 2)?.toLowerCase();
    return SUPPORTED.includes(nav) ? nav : FALLBACK_LANG;
  } catch (e) {
    console.warn('[i18n] Browser language detection failed:', e);
    return FALLBACK_LANG;
  }
}

function getInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch (e) { console.warn('[i18n] localStorage read failed:', e); }
  return detectBrowserLang();
}

function resolve(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

function interpolate(str, params) {
  if (!params || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (_, key) => (params[key] != null ? params[key] : `{${key}}`));
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  const setLang = useCallback((newLang) => {
    if (SUPPORTED.includes(newLang)) {
      setLangState(newLang);
      try { localStorage.setItem(STORAGE_KEY, newLang); } catch (e) { console.warn('[i18n] localStorage write failed:', e); }
    }
  }, []);

  const t = useCallback((key, params) => {
    let val = resolve(LOCALES[lang], key);
    if (val == null && lang !== FALLBACK_LANG) {
      val = resolve(LOCALES[FALLBACK_LANG], key);
    }
    if (val == null) return key;
    return interpolate(val, params);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, supportedLangs: SUPPORTED }), [lang, setLang, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within I18nProvider');
  return ctx;
}
